import {
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

/**
 * Cyclone platform provider: viewer auth, settings, ignore list, and fanpost
 * context. SB Nation only. Heavier than CycloneCommentsProvider, which handles
 * comment counts app-wide for cyclone-comment communities.
 */

import { acknowledgeUserMessage as acknowledgeUserMessageMutation } from "@framework/cyclone/graphql/acknowledge-user-message";
import {
  unignoreUser as unignoreUserMutation,
  ignoreUser as ignoreUserMutation,
} from "@framework/cyclone/graphql/ignoreUser";
import { UserMessageFragment } from "@framework/cyclone/graphql/query";
import { updateUserSettings as updateUserSettingsMutation } from "@framework/cyclone/graphql/update-user-settings";
import { makeRequest } from "@framework/cyclone/request";
import {
  Role,
  User,
  UserRestriction,
  UserStatus,
  UserSettings,
} from "@framework/cyclone/types";
import {
  resolveModeratorScopes,
  type ModeratorScopesQueryData,
} from "@framework/cyclone/viewer/resolve-moderator-scopes";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useCurrentCycloneCommunity } from "@voxmedia/duet-context/context/community-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import CycloneContext from "@voxmedia/duet-context/context/cyclone-context";

const query = `
  query ViewerQuery {
    viewer {
      createdAt
      id
      email
      username
      displayName
      displayNameOrUsername
      profileURL
      role
      avatar
      ignoredUsers {
        id
        user {
          id
          username
        }
      }
      restrictions {
        type
        allCommunities
        communityID
        expiresAt
      }
      activeWarning {
        ${UserMessageFragment}
      }
      activeMessages {
        ${UserMessageFragment}
      }
      communities
      settings {
        fanPostFeedMode
        replySort
        unfurlMediaEmbeds
      }
    }
  }
`;

interface ViewerResponse {
  viewer?: User;
}

/**
 * `User.moderatorScopes` is @auth-gated to MODERATOR/ADMIN. Querying it on the
 * main ViewerQuery would error for regular users and wipe the viewer. Fetch it
 * only after we know the viewer is a moderator.
 */
const moderatorScopesQuery = `
  query ViewerModeratorScopesQuery {
    viewer {
      moderatorScopes {
        communityID
      }
    }
  }
`;

export interface ViewerUserWithToken extends User {
  token?: string | null;
}

const CycloneProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { user, userLoading } = useAuth();
  const [viewer, setViewer] = useState<ViewerUserWithToken | undefined>(
    undefined,
  );
  const [viewerRequest, setViewerRequest] = useState<{
    token: string | null;
    loading: boolean;
  }>({ token: null, loading: false });
  const viewerRequestIDRef = useRef(0);

  const {
    cycloneConfig: { serverURL, enabled },
  } = useConfigContext();

  const { cycloneCommunityId } = useCurrentCycloneCommunity();
  const identityToken = user?.cycloneToken ?? null;
  const resolvedViewer =
    identityToken && viewer?.token === identityToken ? viewer : undefined;
  const viewerLoading =
    userLoading ||
    Boolean(
      identityToken &&
      (viewerRequest.token !== identityToken || viewerRequest.loading),
    );

  const viewerBans: UserRestriction[] = useMemo(() => {
    return (
      resolvedViewer?.restrictions?.filter(
        (r: UserRestriction) =>
          r.type === UserStatus.Banned &&
          (!!r.allCommunities || r.communityID === cycloneCommunityId),
      ) ?? []
    );
  }, [cycloneCommunityId, resolvedViewer?.restrictions]);

  const viewerSuspensions: UserRestriction[] = useMemo(() => {
    return (
      resolvedViewer?.restrictions?.filter(
        (r: UserRestriction) =>
          r.type === UserStatus.Suspended &&
          (!!r.allCommunities || r.communityID === cycloneCommunityId),
      ) ?? []
    );
  }, [cycloneCommunityId, resolvedViewer?.restrictions]);

  const viewerHasUnacknowledgedWarning = !!resolvedViewer?.activeWarning;

  const ignoreUser = useCallback(
    async (id: string) => {
      if (!resolvedViewer?.token) {
        return;
      }
      const response = await ignoreUserMutation(
        id,
        serverURL,
        cycloneCommunityId,
        resolvedViewer.token,
      );
      if (!response) {
        return;
      }

      // Upon a successful ignore, add the user to the viewer's ignored users list
      setViewer((v) => {
        if (!v) {
          return v;
        }
        return {
          ...v,
          ignoredUsers: [
            ...(v.ignoredUsers ?? []),
            {
              id: response.id,
              user: { id: response.user.id, username: response.user.username },
            },
          ],
        };
      });
    },
    [resolvedViewer?.token, serverURL, cycloneCommunityId, setViewer],
  );

  const unignoreUser = useCallback(
    async (id: string) => {
      if (!resolvedViewer?.token) {
        return;
      }
      const response = await unignoreUserMutation(
        id,
        serverURL,
        cycloneCommunityId,
        resolvedViewer.token,
      );
      if (!response) {
        return;
      }

      // Upon successful unignore, remove the user from the viewer's ignored users list
      setViewer((v) => {
        if (!v) {
          return v;
        }
        return {
          ...v,
          ignoredUsers: v.ignoredUsers?.filter((u) => u.id !== id),
        };
      });
    },
    [resolvedViewer?.token, serverURL, cycloneCommunityId, setViewer],
  );

  const acknowledgeUserMessage = useCallback(
    async (id: string) => {
      if (!resolvedViewer?.token) {
        return;
      }

      const response = await acknowledgeUserMessageMutation(
        id,
        cycloneCommunityId,
        serverURL,
        resolvedViewer.token,
      );

      if (!response) {
        return;
      }

      setViewer((v) => {
        if (!v) {
          return v;
        }

        return {
          ...v,
          activeWarning: v.activeWarning?.id === id ? null : v.activeWarning,
          activeMessages: v.activeMessages?.filter(
            (message) => message.id !== id,
          ),
        };
      });
    },
    [resolvedViewer?.token, serverURL, cycloneCommunityId, setViewer],
  );

  const updateUserSettings = useCallback(
    async (id: string, settings: UserSettings) => {
      if (!resolvedViewer?.token) {
        return;
      }
      const response = await updateUserSettingsMutation(
        cycloneCommunityId,
        id,
        settings,
        serverURL,
        resolvedViewer.token,
      );
      if (!response) {
        return;
      }
      setViewer((v) => {
        if (!v) {
          return v;
        }
        return { ...v, settings: { ...v.settings, ...settings } };
      });
    },
    [resolvedViewer?.token, serverURL, cycloneCommunityId, setViewer],
  );

  const loadUser = useCallback(
    async (cycloneToken: string | null) => {
      const requestID = ++viewerRequestIDRef.current;

      if (!cycloneToken) {
        setViewer(undefined);
        setViewerRequest({ token: null, loading: false });
        return;
      }

      setViewerRequest({ token: cycloneToken, loading: true });
      const url = new URL("/api", serverURL);
      let response;
      try {
        response = await makeRequest<ViewerResponse>(
          url.toString(),
          query,
          {},
          cycloneToken,
        );
      } catch {
        if (requestID === viewerRequestIDRef.current) {
          setViewer(undefined);
          setViewerRequest({ token: cycloneToken, loading: false });
        }
        return;
      }

      if (requestID !== viewerRequestIDRef.current) {
        return;
      }

      if (!response.data || !response.data.viewer || response.errors) {
        setViewer(undefined);
        setViewerRequest({ token: cycloneToken, loading: false });
        return;
      }

      let viewer: ViewerUserWithToken = {
        ...response.data.viewer,
        token: cycloneToken,
      };

      if (viewer.role === Role.MODERATOR) {
        try {
          const scopesResponse = await makeRequest<ModeratorScopesQueryData>(
            url.toString(),
            moderatorScopesQuery,
            {},
            cycloneToken,
          );

          if (requestID !== viewerRequestIDRef.current) {
            return;
          }

          viewer = {
            ...viewer,
            moderatorScopes: resolveModeratorScopes(scopesResponse),
          };
        } catch {
          if (requestID !== viewerRequestIDRef.current) {
            return;
          }

          viewer = {
            ...viewer,
            moderatorScopes: null,
          };
        }
      }

      setViewer(viewer);
      setViewerRequest({ token: cycloneToken, loading: false });
    },
    [serverURL],
  );

  useEffect(() => {
    if (!enabled) {
      viewerRequestIDRef.current += 1;
      setViewer(undefined);
      setViewerRequest({ token: identityToken, loading: false });
      return;
    }
    void loadUser(identityToken);
  }, [identityToken, loadUser, enabled]);

  return (
    <CycloneContext.Provider
      value={{
        viewer: resolvedViewer ?? null,
        viewerLoading,
        viewerBans,
        viewerSuspensions,
        viewerHasUnacknowledgedWarning,
        unignoreUser,
        ignoreUser,
        updateUserSettings,
        acknowledgeUserMessage,
      }}
    >
      {children}
    </CycloneContext.Provider>
  );
};

export default CycloneProvider;
